/**
 * V70.28 — 인력 현황 Dashboard 전용 경량 차트 (CSS + SVG 자체 구현)
 *
 * 신규 chart dependency 를 추가하지 않는다. 범용 chart framework 가 아니라
 * 이 Dashboard 가 필요한 3가지만 구현한다.
 *
 *  1) EmploymentLineChart — 올해 1~12월 고용형태별 인원 추이 (V70.29: Stacked Bar → Line)
 *  2) DonutChart         — 현재 고용형태 비율
 *  3) JoinLeaveChart     — 월별 입사/퇴사(실적) + 예정 + 순증감
 *
 * 미래 월(예정치)은 **점선 + 빈 원 + 배경 음영**으로 실적과 시각적으로 구분한다.
 *
 * V70.32 hover UX
 *  - Donut: 크기 변화 없이 **비선택 segment 디밍**만으로 강조 (segment ↔ legend 양방향 연동)
 *  - JoinLeave: tooltip 을 **스크롤 컨테이너 밖 overlay layer** 로 분리하고 flip/clamp 적용
 *
 * 공통 원칙
 *  - 색상은 `palette.ts` 단일 정의만 사용
 *  - 색상만으로 구분하지 않고 label/수치를 항상 함께 제공
 *  - 넓은 차트는 부모에서 `overflow-x: auto` 로 감싼다 (가로 스크롤은 컨테이너 안에서만)
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import {
  EMPLOYMENT_TYPE_ORDER, EmploymentTypeKey, typeStyle,
  JOIN_COLOR, LEAVE_COLOR, CHART_GRID, CHART_AXIS_TEXT,
  CONTRACT_END_ORDER, contractEndStyle, CONTRACT_END_GROUND,
} from './palette';

// ============================================================
// V70.30: 컨테이너 실제 폭 측정 (responsive 차트용)
//   ResizeObserver 로 부모 폭을 측정해 **SVG 내부 좌표계를 실제 폭으로 다시 계산**한다.
//   CSS 로 SVG 만 늘리면 hover 히트박스/tooltip 위치가 어긋나므로 그 방식은 쓰지 않는다.
//   ResizeObserver 미지원 환경은 window resize 로 폴백한다. (신규 의존성 없음)
// ============================================================
function useContainerWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => setWidth(el.clientWidth);
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => measure());
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return [ref, width];
}

// ============================================================
// 1) Line Chart — 올해 1~12월 고용형태별 인원 추이 (V70.29)
//    고용형태 4개를 각각 line 으로 표현. 미래 월은 점선 + 빈 원으로 구분.
//    V70.30: 컨테이너 폭에 맞춰 **좌표계를 다시 계산**하는 responsive 차트.
//            폭이 MIN_WIDTH 보다 좁을 때만 가로 스크롤로 전환한다.
// ============================================================
export interface TrendPoint {
  ym: string;
  /** 'N월' */
  label: string;
  month: number;
  basis: 'monthEnd' | 'today' | 'projected';
  total: number;
  byType: Record<string, number>;
}

/** 가로 스크롤 없이 12개월을 읽을 수 있는 최소 폭 */
const LINE_MIN_WIDTH = 560;

export function EmploymentLineChart({ data }: { data: TrendPoint[] }) {
  const [wrapRef, measuredW] = useContainerWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  if (data.length === 0) return null;

  // ── 실제 렌더 크기 (컨테이너 폭 기반) ──
  // 첫 렌더에서는 measuredW=0 이므로 최소 폭으로 그린 뒤 측정값으로 즉시 재렌더된다.
  const w = Math.max(measuredW || LINE_MIN_WIDTH, LINE_MIN_WIDTH);
  // 높이: clamp(360px, 폭의 30%, 440px)
  //   - 1920px 뷰포트 → 440 (상한)
  //   - 1440 / 1280px → 360 (하한) : 경영진 Desktop 가독성 최소치
  const height = Math.round(Math.max(360, Math.min(w * 0.30, 440)));

  // ── 레이아웃 ──
  const padL = 44;
  const padR = 16;
  const padT = 18;
  const padB = 40;
  const innerW = w - padL - padR;
  const plotH = height - padT - padB;

  const rawMax = Math.max(1, ...data.map((d) => d.total));
  // y축 눈금을 보기 좋은 단위로 올림
  const step = rawMax <= 10 ? 2 : rawMax <= 50 ? 10 : rawMax <= 200 ? 20 : 50;
  const yMax = Math.ceil(rawMax / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= yMax; v += step) ticks.push(v);

  const x = (i: number) => padL + (innerW / data.length) * (i + 0.5);
  const y = (v: number) => padT + plotH - (v / yMax) * plotH;

  // 미래 구간 시작 index (projected 첫 지점)
  const firstFuture = data.findIndex((d) => d.basis === 'projected');
  const futureStartX = firstFuture > 0 ? (x(firstFuture - 1) + x(firstFuture)) / 2 : null;

  return (
    // wrapRef 가 가용 폭을 측정한다. 컨테이너가 LINE_MIN_WIDTH 보다 좁을 때만 가로 스크롤.
    <div ref={wrapRef} style={{ width: '100%', overflowX: 'auto' }}>
      <div style={{ width: w, position: 'relative' }}>
        <svg width={w} height={height} role="img" aria-label="고용형태별 인력 추이" style={{ display: 'block' }}>
          {/* 미래 구간 배경 음영 */}
          {futureStartX !== null && (
            <rect
              x={futureStartX} y={padT} width={padL + innerW - futureStartX} height={plotH}
              fill="#f8fafc"
            />
          )}
          {/* y 그리드 + 라벨 */}
          {ticks.map((v) => (
            <g key={v}>
              <line x1={padL} y1={y(v)} x2={padL + innerW} y2={y(v)} stroke={CHART_GRID} strokeWidth={1} />
              <text x={padL - 6} y={y(v) + 3.5} textAnchor="end" fontSize={10} fill={CHART_AXIS_TEXT}>{v}</text>
            </g>
          ))}
          {/* hover 가이드 */}
          {hover !== null && (
            <line x1={x(hover)} y1={padT} x2={x(hover)} y2={padT + plotH} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" />
          )}

          {/* 고용형태별 line */}
          {EMPLOYMENT_TYPE_ORDER.map((code) => {
            const color = typeStyle(code).color;
            const pts = data.map((d, i) => ({ i, cx: x(i), cy: y(d.byType[code] || 0), future: d.basis === 'projected' }));
            // 실적 구간 / 예정 구간을 나눠 그린다 (경계 지점은 양쪽에 모두 포함)
            const solid = pts.filter((p) => !p.future);
            const lastSolidIdx = solid.length > 0 ? solid[solid.length - 1].i : -1;
            const dashed = pts.filter((p) => p.future || p.i === lastSolidIdx);
            const toPath = (arr: typeof pts) => arr.map((p) => `${p.cx},${p.cy}`).join(' ');
            return (
              <g key={code}>
                {solid.length > 1 && (
                  <polyline points={toPath(solid)} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
                )}
                {dashed.length > 1 && (
                  <polyline points={toPath(dashed)} fill="none" stroke={color} strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />
                )}
                {pts.map((p) => (
                  <circle
                    key={p.i}
                    cx={p.cx} cy={p.cy}
                    r={hover === p.i ? 4.5 : 3}
                    fill={p.future ? 'white' : color}
                    stroke={color}
                    strokeWidth={p.future ? 2 : 1}
                  />
                ))}
              </g>
            );
          })}

          {/* x 라벨 */}
          {data.map((d, i) => (
            <g key={d.ym}>
              <text
                x={x(i)} y={height - padB + 15} textAnchor="middle" fontSize={11}
                fill={d.basis === 'today' ? 'var(--color-gray-900)' : CHART_AXIS_TEXT}
                fontWeight={d.basis === 'today' ? 700 : 400}
              >
                {d.label}
              </text>
              {d.basis !== 'monthEnd' && (
                <text x={x(i)} y={height - padB + 27} textAnchor="middle" fontSize={9} fill={CHART_AXIS_TEXT}>
                  {d.basis === 'today' ? '현재' : '예정'}
                </text>
              )}
            </g>
          ))}

          {/* hover 히트박스 */}
          {data.map((d, i) => (
            <rect
              key={`hit-${d.ym}`}
              x={padL + (innerW / data.length) * i} y={padT}
              width={innerW / data.length} height={plotH}
              fill="transparent"
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>

        {/* tooltip */}
        {hover !== null && (
          <div
            style={{
              position: 'absolute',
              left: Math.min(Math.max(x(hover), 90), w - 90),
              top: padT,
              transform: 'translate(-50%, -4px)',
              pointerEvents: 'none', zIndex: 20,
            }}
          >
            <div
              style={{
                background: 'rgba(17,24,39,0.95)', color: 'white', padding: '8px 10px',
                borderRadius: 6, fontSize: 12, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                {data[hover].ym.slice(0, 4)}년 {data[hover].month}월
                {data[hover].basis === 'today' && ' (현재)'}
                {data[hover].basis === 'projected' && ' (예정)'}
              </div>
              {EMPLOYMENT_TYPE_ORDER.map((code) => (
                <div key={code} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: typeStyle(code).color, flexShrink: 0 }} />
                  <span style={{ opacity: 0.85 }}>{typeStyle(code).label}</span>
                  <span style={{ marginLeft: 'auto', paddingLeft: 12 }}>{data[hover].byType[code] || 0}명</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: 5, paddingTop: 5, borderTop: '1px solid rgba(255,255,255,0.2)', fontWeight: 700 }}>
                <span>총</span>
                <span style={{ marginLeft: 'auto', paddingLeft: 12 }}>{data[hover].total}명</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** 실적 / 예정 구분 범례 */
export function ActualProjectedLegend() {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, fontSize: 11.5, color: CHART_AXIS_TEXT }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke={CHART_AXIS_TEXT} strokeWidth="2" /></svg>
        실적
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke={CHART_AXIS_TEXT} strokeWidth="2" strokeDasharray="5 4" /></svg>
        예정
      </span>
    </span>
  );
}

// ============================================================
// 2) Donut — 현재 고용형태 비율
// ============================================================
export interface DonutSlice {
  code: string;
  label: string;
  count: number;
  ratio: number;
}

/**
 * V70.32 hover 정책
 *   - **크기/두께를 바꾸지 않는다.** (기존 `strokeWidth + 4` 는 반지름이 (size-thickness)/2 라
 *     외곽이 SVG 경계에 닿아 있어 두꺼워진 부분이 잘리면서 "튀는" 느낌을 만들었다)
 *   - 선택 segment 는 palette 원색 유지(opacity 1), 나머지만 약하게 디밍
 *   - segment ↔ legend 양방향 연동 (같은 hover state 공유)
 *   - `transition: opacity 180ms ease` 로 절제된 애니메이션
 */
const DIM_OPACITY = 0.4;
const DIM_LEGEND_OPACITY = 0.45;
const HOVER_TRANSITION = 'opacity 180ms ease, border-color 180ms ease';

export function DonutChart({ slices, total, size = 180, thickness = 26 }: {
  slices: DonutSlice[]; total: number; size?: number; thickness?: number;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  const drawable = slices.filter((s) => s.count > 0);
  /** hover 중이고 내가 대상이 아니면 디밍 */
  const dimmed = (code: string) => !!hover && hover !== code;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} role="img" aria-label="고용형태 비율">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={CHART_GRID} strokeWidth={thickness} />
          {total > 0 && drawable.map((s) => {
            const len = (s.count / total) * c;
            const el = (
              <circle
                key={s.code}
                cx={size / 2} cy={size / 2} r={r} fill="none"
                stroke={typeStyle(s.code).color}
                /* 두께 고정 — hover 로 크기를 바꾸지 않는다 */
                strokeWidth={thickness}
                strokeDasharray={`${len} ${c - len}`}
                strokeDashoffset={-offset}
                opacity={dimmed(s.code) ? DIM_OPACITY : 1}
                onMouseEnter={() => setHover(s.code)}
                onMouseLeave={() => setHover(null)}
                style={{ transition: HOVER_TRANSITION, cursor: 'default' }}
              />
            );
            offset += len;
            return el;
          })}
        </svg>
        <div
          style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
          }}
        >
          <div style={{ fontSize: 11, color: CHART_AXIS_TEXT }}>총</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-gray-900)', lineHeight: 1.1 }}>
            {total}
          </div>
          <div style={{ fontSize: 11, color: CHART_AXIS_TEXT }}>명</div>
        </div>
      </div>

      {/* legend — 색상만으로 구분하지 않도록 label + 수치 항상 표기.
          hover 시 색 블록을 깔지 않고 **좌측 2px 액센트 + 나머지 디밍** 으로만 강조한다. */}
      <div style={{ flex: 1, minWidth: 180 }}>
        {slices.map((s) => (
          <div
            key={s.code}
            onMouseEnter={() => setHover(s.code)}
            onMouseLeave={() => setHover(null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 8px', fontSize: 13,
              borderLeft: `2px solid ${hover === s.code ? typeStyle(s.code).color : 'transparent'}`,
              opacity: dimmed(s.code) ? DIM_LEGEND_OPACITY : 1,
              transition: HOVER_TRANSITION,
              cursor: 'default',
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: 2, background: typeStyle(s.code).color, flexShrink: 0 }} />
            <span style={{ color: 'var(--color-gray-700)', fontWeight: hover === s.code ? 600 : 400 }}>{s.label}</span>
            <span style={{ marginLeft: 'auto', fontWeight: 600, color: 'var(--color-gray-900)' }}>{s.count}명</span>
            <span style={{ width: 52, textAlign: 'right', color: CHART_AXIS_TEXT }}>{s.ratio.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// 3) 월별 입사/퇴사 Bar — 실적 + 예정
//    실적은 채운 bar, 예정은 같은 색 사선 패턴 bar 로 구분한다.
//
//    V70.32 — Tooltip 잘림 수정
//      [원인] 기존 tooltip 은 `position:absolute; bottom:100%` 로 각 월 컬럼 위에 그려졌고,
//             그 부모가 `overflow-x: auto` 였다.
//             CSS 규격상 한 축이 auto 면 다른 축의 `visible` 도 auto 로 계산되어
//             **상·하 양방향 모두 클리핑**된다. 행의 padding-top 이 20px 뿐이라
//             약 110px 높이 tooltip 의 상단 대부분이 잘려 하단만 보였다.
//      [수정] 스크롤 컨테이너 **밖**의 overlay layer 에 tooltip 을 렌더링하고,
//             실제 tooltip 크기를 측정해 flip(상↔하) / clamp(좌우) 를 적용한다.
//             z-index 땜질이 아니라 clipping 주체를 분리한 구조 변경이다.
// ============================================================
export interface JoinLeavePoint {
  ym: string;
  label: string;
  month: number;
  basis: 'past' | 'current' | 'future';
  joins: number;
  joinsScheduled: number;
  leaves: number;
  leavesScheduled: number;
  net: number;
}

/** 가로 스크롤 없이 12개월을 읽을 수 있는 최소 폭 */
const JL_MIN_WIDTH = 480;
const JL_GAP = 8;          // 컬럼 간격
const JL_PAD_X = 4;        // 좌우 패딩
const JL_PAD_TOP = 20;     // bar 영역 위 여백
const JL_VALUE_LABEL_H = 14; // bar 위 숫자 라벨 높이
const JL_XAXIS_H = 30;     // x축 라벨 영역 높이
const JL_TIP_GAP = 10;     // bar 와 tooltip 간격

export function JoinLeaveChart({ data, height = 210 }: { data: JoinLeavePoint[]; height?: number }) {
  const [wrapRef, measuredW] = useContainerWidth<HTMLDivElement>();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [tipSize, setTipSize] = useState<{ w: number; h: number } | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  // tooltip 실제 크기 측정 (구조가 일정하므로 첫 측정값을 재사용 → 깜빡임 없음)
  useLayoutEffect(() => {
    if (hover === null) return;
    const el = tipRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (!tipSize || Math.abs(tipSize.w - w) > 1 || Math.abs(tipSize.h - h) > 1) {
      setTipSize({ w, h });
    }
  }, [hover, tipSize]);

  const n = data.length;
  const chartW = Math.max(measuredW || JL_MIN_WIDTH, JL_MIN_WIDTH);
  const plot = height - 34;
  const contentW = chartW - JL_PAD_X * 2;
  const colW = n > 0 ? (contentW - JL_GAP * (n - 1)) / n : 0;
  const totalH = height + JL_XAXIS_H;

  const max = Math.max(1, ...data.map((d) =>
    Math.max(d.joins + d.joinsScheduled, d.leaves + d.leavesScheduled)));
  const barPx = (v: number) => Math.round((v / max) * (plot - 16));

  /** 컬럼 i 의 중심 x (스크롤 보정 포함) */
  const centerX = (i: number) => JL_PAD_X + i * (colW + JL_GAP) + colW / 2 - scrollLeft;
  /** 컬럼 i 의 가장 높은 bar 상단 y */
  const barTopY = (d: JoinLeavePoint) => {
    const tallest = Math.max(d.joins + d.joinsScheduled, d.leaves + d.leavesScheduled);
    const labelH = tallest > 0 ? JL_VALUE_LABEL_H : 0;
    return JL_PAD_TOP + plot - barPx(tallest) - labelH;
  };

  // ── tooltip 위치: 기본 bar 위 → 공간 부족 시 아래로 flip, 좌우는 clamp ──
  let tipLeft = 0;
  let tipTop = 0;
  let flipped = false;
  if (hover !== null && tipSize) {
    const d = data[hover];
    const half = tipSize.w / 2;
    // 좌우 clamp — chart block 밖으로 나가지 않게
    tipLeft = Math.min(Math.max(centerX(hover), half + 2), Math.max(half + 2, (measuredW || chartW) - half - 2));
    const above = barTopY(d) - tipSize.h - JL_TIP_GAP;
    if (above >= 0) {
      tipTop = above;
    } else {
      // 상단 공간 부족 → 아래로 flip. 아래로도 넘치면 block 안으로 clamp
      flipped = true;
      tipTop = Math.min(barTopY(d) + JL_TIP_GAP + JL_VALUE_LABEL_H, totalH - tipSize.h - 2);
      if (tipTop < 0) tipTop = 0;
    }
  }

  const Bar = ({ actual, scheduled, color }: { actual: number; scheduled: number; color: string }) => {
    const totalV = actual + scheduled;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
        {totalV > 0 && <div style={{ fontSize: 10, color, fontWeight: 600, height: JL_VALUE_LABEL_H }}>{totalV}</div>}
        <div style={{ width: 14, display: 'flex', flexDirection: 'column-reverse' }}>
          {actual > 0 && (
            <div style={{ height: barPx(actual), minHeight: 2, background: color, borderRadius: scheduled > 0 ? 0 : '2px 2px 0 0' }} />
          )}
          {scheduled > 0 && (
            <div
              title="예정"
              style={{
                height: barPx(scheduled), minHeight: 2, borderRadius: '2px 2px 0 0',
                border: `1px solid ${color}`, borderBottom: actual > 0 ? 'none' : `1px solid ${color}`,
                background: `repeating-linear-gradient(45deg, ${color} 0 3px, transparent 3px 6px)`,
              }}
            />
          )}
        </div>
      </div>
    );
  };

  const d = hover !== null ? data[hover] : null;

  return (
    // overlay layer 부모 — overflow 를 걸지 않는다 (clipping 주체 분리)
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      <div
        ref={scrollRef}
        onScroll={(e) => setScrollLeft((e.target as HTMLDivElement).scrollLeft)}
        style={{ width: '100%', overflowX: 'auto' }}
      >
        <div style={{ width: chartW }}>
          <div
            style={{
              display: 'flex', gap: JL_GAP, alignItems: 'flex-end',
              height, borderBottom: `1px solid ${CHART_GRID}`,
              padding: `${JL_PAD_TOP}px ${JL_PAD_X}px 0`, boxSizing: 'border-box',
            }}
          >
            {data.map((p, i) => (
              <div
                key={p.ym}
                style={{
                  width: colW, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  background: p.basis === 'future' ? '#f8fafc' : 'transparent',
                }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: plot, width: '100%', justifyContent: 'center' }}>
                  <Bar actual={p.joins} scheduled={p.joinsScheduled} color={JOIN_COLOR} />
                  <Bar actual={p.leaves} scheduled={p.leavesScheduled} color={LEAVE_COLOR} />
                </div>
                <div style={{
                  fontSize: 10, marginTop: 4, fontWeight: 600,
                  color: p.net > 0 ? JOIN_COLOR : p.net < 0 ? LEAVE_COLOR : CHART_AXIS_TEXT,
                }}>
                  {p.net > 0 ? `+${p.net}` : p.net < 0 ? `${p.net}` : '0'}
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: JL_GAP, padding: `6px ${JL_PAD_X}px 0`, boxSizing: 'border-box' }}>
            {data.map((p) => (
              <div
                key={p.ym}
                style={{
                  width: colW, textAlign: 'center', fontSize: 10.5,
                  color: p.basis === 'current' ? 'var(--color-gray-900)' : CHART_AXIS_TEXT,
                  fontWeight: p.basis === 'current' ? 700 : 400,
                }}
              >
                {p.label}
                {p.basis !== 'past' && (
                  <div style={{ fontSize: 9, color: CHART_AXIS_TEXT }}>
                    {p.basis === 'current' ? '현재' : '예정'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── tooltip overlay (스크롤 컨테이너 밖) ── */}
      {d && (
        <div
          ref={tipRef}
          data-testid="jl-tooltip"
          style={{
            position: 'absolute',
            left: tipSize ? tipLeft : -9999,
            top: tipSize ? tipTop : 0,
            transform: 'translateX(-50%)',
            // 측정 전 1프레임 노출 방지
            opacity: tipSize ? 1 : 0,
            transition: 'opacity 120ms ease',
            // hover 를 가로채 깜빡임이 생기지 않도록
            pointerEvents: 'none',
            zIndex: 30,
            background: 'rgba(17,24,39,0.95)', color: 'white',
            padding: '9px 11px', borderRadius: 6, fontSize: 12,
            whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(0,0,0,0.20)',
            minWidth: 150,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, paddingBottom: 5, borderBottom: '1px solid rgba(255,255,255,0.18)' }}>
            {d.ym.slice(0, 4)}년 {d.month}월
            {d.basis === 'current' && <span style={{ fontWeight: 400, opacity: 0.75 }}> · 진행중</span>}
            {d.basis === 'future' && <span style={{ fontWeight: 400, opacity: 0.75 }}> · 예정</span>}
          </div>
          <TipRow color={JOIN_COLOR} label="입사" value={d.joins} />
          {d.joinsScheduled > 0 && <TipRow color={JOIN_COLOR} label="입사 예정" value={d.joinsScheduled} scheduled />}
          <TipRow color={LEAVE_COLOR} label="퇴사" value={d.leaves} />
          {d.leavesScheduled > 0 && <TipRow color={LEAVE_COLOR} label="퇴사 예정" value={d.leavesScheduled} scheduled />}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, paddingTop: 5,
            borderTop: '1px solid rgba(255,255,255,0.18)', fontWeight: 700,
          }}>
            <span style={{ opacity: 0.85 }}>순증감</span>
            <span style={{ marginLeft: 'auto' }}>{d.net > 0 ? `+${d.net}` : `${d.net}`}명</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** tooltip 한 줄 — 예정 항목은 사선 패턴 swatch 로 실적과 구분 */
function TipRow({ color, label, value, scheduled }: {
  color: string; label: string; value: number; scheduled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
      <span
        style={{
          width: 9, height: 9, borderRadius: 2, flexShrink: 0,
          ...(scheduled
            ? { border: `1px solid ${color}`, background: `repeating-linear-gradient(45deg, ${color} 0 3px, transparent 3px 6px)` }
            : { background: color }),
        }}
      />
      <span style={{ opacity: 0.85 }}>{label}</span>
      <span style={{ marginLeft: 'auto', paddingLeft: 14, fontWeight: 600 }}>{value}명</span>
    </div>
  );
}

/** 입사/퇴사 범례 (실적/예정 구분 포함) */
export function JoinLeaveLegend() {
  const swatch = (color: string, scheduled: boolean) => ({
    width: 10, height: 10, borderRadius: 2,
    ...(scheduled
      ? { border: `1px solid ${color}`, background: `repeating-linear-gradient(45deg, ${color} 0 3px, transparent 3px 6px)` }
      : { background: color }),
  } as React.CSSProperties);
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--color-gray-600)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={swatch(JOIN_COLOR, false)} /> 입사
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={swatch(LEAVE_COLOR, false)} /> 퇴사
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span style={swatch(CHART_AXIS_TEXT, true)} /> 예정
      </span>
    </div>
  );
}

/** 고용형태 범례 (line chart 용) */
export function EmploymentTypeLegend() {
  return (
    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--color-gray-600)' }}>
      {EMPLOYMENT_TYPE_ORDER.map((c: EmploymentTypeKey) => (
        <span key={c} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: typeStyle(c).color }} />
          {typeStyle(c).label}
        </span>
      ))}
    </div>
  );
}

// ============================================================
// 4) 월별 계약 만료 Stacked Bar (V70.35 → V70.37)
//    막대 **전체 높이 = 만료 도래(total)** 로 유지하고,
//    색 세그먼트로는 확정 결과(종료 / 전환 / 연장)만 쌓는다.
//    확정되지 않은 잔여분은 **상태 라벨 없는 바탕**으로 남긴다
//    (V70.36 의 '미결' 세그먼트 제거 — 결과를 추측해 보여주지 않는다).
//    Tooltip 은 V70.32 의 overlay + flip/clamp 방식을 그대로 재사용한다
//    (스크롤 컨테이너 밖 layer → 상단 clipping 재발 방지).
// ============================================================
export interface ContractEndPoint {
  ym: string;
  label: string;
  month: number;
  converted: number;
  ended: number;
  extended: number;
  /** 확정 대상 = 전환 + 연장 + 종료 (비율 분모) */
  decided: number;
  total: number;
}

export function ContractEndChart({ data, height = 230 }: { data: ContractEndPoint[]; height?: number }) {
  const [wrapRef, measuredW] = useContainerWidth<HTMLDivElement>();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [tipSize, setTipSize] = useState<{ w: number; h: number } | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  useLayoutEffect(() => {
    if (hover === null) return;
    const el = tipRef.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (!tipSize || Math.abs(tipSize.w - w) > 1 || Math.abs(tipSize.h - h) > 1) setTipSize({ w, h });
  }, [hover, tipSize]);

  const n = data.length;
  const chartW = Math.max(measuredW || JL_MIN_WIDTH, JL_MIN_WIDTH);
  const plot = height - 34;
  const contentW = chartW - JL_PAD_X * 2;
  const colW = n > 0 ? (contentW - JL_GAP * (n - 1)) / n : 0;
  const totalH = height + JL_XAXIS_H;

  const max = Math.max(1, ...data.map((d) => d.total));
  const barPx = (v: number) => Math.round((v / max) * (plot - 16));

  const centerX = (i: number) => JL_PAD_X + i * (colW + JL_GAP) + colW / 2 - scrollLeft;
  const barTopY = (d: ContractEndPoint) =>
    JL_PAD_TOP + plot - barPx(d.total) - (d.total > 0 ? JL_VALUE_LABEL_H : 0);

  let tipLeft = 0;
  let tipTop = 0;
  if (hover !== null && tipSize) {
    const d = data[hover];
    const half = tipSize.w / 2;
    tipLeft = Math.min(Math.max(centerX(hover), half + 2), Math.max(half + 2, (measuredW || chartW) - half - 2));
    const above = barTopY(d) - tipSize.h - JL_TIP_GAP;
    if (above >= 0) {
      tipTop = above;
    } else {
      tipTop = Math.min(barTopY(d) + JL_TIP_GAP + JL_VALUE_LABEL_H, totalH - tipSize.h - 2);
      if (tipTop < 0) tipTop = 0;
    }
  }

  const d = hover !== null ? data[hover] : null;

  return (
    <div ref={wrapRef} style={{ width: '100%', position: 'relative' }}>
      <div
        ref={scrollRef}
        onScroll={(e) => setScrollLeft((e.target as HTMLDivElement).scrollLeft)}
        style={{ width: '100%', overflowX: 'auto' }}
      >
        <div style={{ width: chartW }}>
          <div
            style={{
              display: 'flex', gap: JL_GAP, alignItems: 'flex-end',
              height, borderBottom: `1px solid ${CHART_GRID}`,
              padding: `${JL_PAD_TOP}px ${JL_PAD_X}px 0`, boxSizing: 'border-box',
            }}
          >
            {data.map((p, i) => (
              <div
                key={p.ym}
                style={{ width: colW, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover(null)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end', height: plot, width: '100%', justifyContent: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                    {p.total > 0 && (
                      <div style={{ fontSize: 10, color: 'var(--color-gray-700)', fontWeight: 600, height: JL_VALUE_LABEL_H }}>
                        {p.total}
                      </div>
                    )}
                    {/*
                      막대 전체 = 만료 도래(total). 아래→위로 확정 결과만 쌓는다:
                      종료 → 전환 → 연장. 남는 위쪽 공간은 '확정 결과 없음' 이며
                      별도 상태로 라벨하지 않는다 (V70.37).
                    */}
                    <div
                      style={{
                        width: 22, height: barPx(p.total), display: 'flex', flexDirection: 'column-reverse',
                        borderRadius: '2px 2px 0 0', overflow: 'hidden',
                        background: p.total > 0 ? CONTRACT_END_GROUND : 'transparent',
                        outline: hover === i ? '2px solid rgba(63,95,138,0.3)' : 'none',
                      }}
                    >
                      {CONTRACT_END_ORDER.map((k) => {
                        const v = p[k];
                        if (!v) return null;
                        // 막대 내부 비율로 계산한다. 세그먼트마다 px 를 반올림하면
                        // 합이 barPx(total) 을 1~2px 초과해 상단 세그먼트가 잘릴 수 있다.
                        return (
                          <div
                            key={k}
                            title={`${contractEndStyle(k).label} ${v}건`}
                            style={{
                              height: `${(v / p.total) * 100}%`,
                              minHeight: 2,
                              flexShrink: 0,
                              background: contractEndStyle(k).color,
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: JL_GAP, padding: `6px ${JL_PAD_X}px 0`, boxSizing: 'border-box' }}>
            {data.map((p) => (
              <div key={p.ym} style={{ width: colW, textAlign: 'center', fontSize: 10.5, color: CHART_AXIS_TEXT }}>
                {p.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {d && (
        <div
          ref={tipRef}
          data-testid="ce-tooltip"
          style={{
            position: 'absolute',
            left: tipSize ? tipLeft : -9999,
            top: tipSize ? tipTop : 0,
            transform: 'translateX(-50%)',
            opacity: tipSize ? 1 : 0,
            transition: 'opacity 120ms ease',
            pointerEvents: 'none',
            zIndex: 30,
            background: 'rgba(17,24,39,0.95)', color: 'white',
            padding: '9px 11px', borderRadius: 6, fontSize: 12,
            whiteSpace: 'nowrap', boxShadow: '0 4px 14px rgba(0,0,0,0.20)', minWidth: 160,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6, paddingBottom: 5, borderBottom: '1px solid rgba(255,255,255,0.18)' }}>
            {d.ym.slice(0, 4)}년 {d.month}월
            <span style={{ marginLeft: 8, fontWeight: 400, opacity: 0.8 }}>만료 도래 {d.total}건</span>
          </div>
          {CONTRACT_END_ORDER.map((k) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: contractEndStyle(k).color, flexShrink: 0 }} />
              <span style={{ opacity: 0.85 }}>{contractEndStyle(k).label}</span>
              <span style={{ marginLeft: 'auto', paddingLeft: 14, fontWeight: 600 }}>{d[k]}</span>
            </div>
          ))}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 6,
            paddingTop: 5, borderTop: '1px solid rgba(255,255,255,0.18)', opacity: 0.8,
          }}>
            <span style={{ fontSize: 11.5 }}>확정 대상</span>
            <span style={{ marginLeft: 'auto', paddingLeft: 14, fontWeight: 600 }}>{d.decided}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/** 계약 만료 결과 범례 */
export function ContractEndLegend() {
  return (
    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--color-gray-600)' }}>
      {CONTRACT_END_ORDER.map((k) => (
        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, borderRadius: 2, background: contractEndStyle(k).color }} />
          {contractEndStyle(k).label}
        </span>
      ))}
    </div>
  );
}
